<template>
  <div :style="`height: calc(100vh - ${mdAndUp ? '64px' : '56px'});`">
    <VMap></VMap>
    <div v-if="!reactivateAccount" class="add-device">
      <RegisterMoveDevice />
      <RegisterDeviceMapButton @open="dialogs.registerDevice = true" />
      <RegisterDevice
        :open="dialogs.registerDevice"
        :deviceSerial="''"
        :deviceModel="''"
        @close="dialogs.registerDevice = false"
        @deviceCreated="onDeviceCreated"
      />
    </div>
    <div class="map-legend">
      <MapLegend />
    </div>
    <FusionReactivateAccountDialog :open="reactivateAccount"></FusionReactivateAccountDialog>
    <div v-if="!reactivateAccount">
      <FusionLatestReleaseNoteDialog
        :open="dialogs.releaseNotes"
        @close="dialogs.releaseNotes = false"
      />
      <SubscriptionExpired
        :open="dialogs.subscriptionExpired"
        @close="dialogs.subscriptionExpired = false"
      />
      <DevicesUpdatedDialog
        :devices="devicesUpdated"
        :open="dialogs.devicesUpdated"
        @close="dialogs.devicesUpdated = false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import semver from 'semver'
import { useDisplay } from 'vuetify'
import { FusionMixins } from '@fusion/saas-components-v3'
import { NAMESPACES } from '@/store/types'
import { USER_ACTIONS, USER_GETTERS } from '@/store/user'
import { SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import { MAP_ACTIONS } from '@/store/map'
import { DEVICE_MODELS_ACTIONS } from '@/store/devicemodels'
import type { UpdatedDevice } from '@/types/user'
import { addUserSetting } from '@/services/user-settings'
import { getModelsMetaData } from '@/services/device-service'
import RegisterDevice from '@/components/registerDevice/registerDialog.vue'
import RegisterDeviceMapButton from '@/components/registerDevice/mapButton.vue'
import RegisterMoveDevice from '@/components/registerDevice/moveDevice.vue'
import VMap from '@/components/Map.vue'
import MapLegend from '@/components/MapLegend.vue'
import SubscriptionExpired from '@/components/dialogs/SubscriptionExpired.vue'
import DevicesUpdatedDialog from '@/components/dialogs/DevicesUpdatedDialog.vue'

export default defineComponent({
  name: 'HomePrivate',
  components: {
    RegisterDevice,
    VMap,
    MapLegend,
    SubscriptionExpired,
    DevicesUpdatedDialog,
    RegisterDeviceMapButton,
    RegisterMoveDevice
  },
  setup() {
    const { mdAndUp } = useDisplay()
    const { reactivateAccount } = FusionMixins.reactivateAccount()
    return { mdAndUp, reactivateAccount }
  },
  data() {
    return {
      dialogs: {
        releaseNotes: false as boolean,
        registerDevice: false as boolean,
        subscriptionExpired: false as boolean,
        devicesUpdated: false as boolean
      }
    }
  },
  async mounted() {
    // if the user has never visited before, setup first visit (show register device)
    if (this.visited === false) {
      await this.setupFirstVisit()
    }
  },
  watch: {
    viewedSubscriptionExpired() {
      //only try if user has already visited
      if (this.$store.state.user.settings.visited) {
        //only do this if the user has not viewed subscription expired
        if (this.viewedSubscriptionExpired === false) {
          //show the dialog
          this.dialogs.subscriptionExpired = true
          //update the setting that we viewed it
          this.updateViewedSubscriptionExpired(true)
        }
      }
    },
    lastVersion() {
      if (!this.$store.state.user.settings.visited) {
        this.updateLVVersion()
        return
      }

      const lrm = this.getLastReleaseMinorV()

      if (semver.satisfies(this.$store.state.releaseVersion, '>' + lrm)) {
        this.updateLVVersion()
        this.dialogs.releaseNotes = true
        return
      }

      if (semver.gt(this.$store.state.releaseVersion, this.lastVersion)) {
        this.updateLVVersion()
      }
    },
    devicesUpdated() {
      //only try if user has already visited
      if (this.$store.state.user.settings.visited) {
        //only do this if the user has not viewed subscription expired
        if (this.devicesUpdated.length > 0) {
          //show the dialog
          this.dialogs.devicesUpdated = true
          //update the setting that we viewed it
          this.updateDevicesUpdated()
        }
      }
    },
    async visited(visited: boolean) {
      if (visited === false) {
        await this.setupFirstVisit()
      }
    }
  },
  computed: {
    lastVersion(): string {
      return this.$store.getters[`${NAMESPACES.USER}/${USER_GETTERS.GET_LAST_VIEWED_VERSION}`]
    },
    viewedSubscriptionExpired(): boolean {
      return this.$store.getters[
        `${NAMESPACES.USER}/${USER_GETTERS.GET_VIEWED_SUBSCRIPTION_EXPIRED}`
      ]
    },
    devicesUpdated(): UpdatedDevice[] {
      return this.$store.getters[`${NAMESPACES.USER}/${USER_GETTERS.GET_DEVICES_UPDATED}`]
    },
    visited(): boolean {
      return this.$store.getters[`${NAMESPACES.USER}/${USER_GETTERS.GET_VISITED}`]
    }
  },
  methods: {
    async setupFirstVisit(): Promise<void> {
      await this.getModelsMeta()
      await this.getUserDevices()
      await this.$store.dispatch(
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
        {
          auth: this.$auth,
          api: this.$api,
          devices: []
        }
      )

      const visitedSetting = addUserSetting({
        visited: true
      })
      this.$api.patchUserSettings(visitedSetting)
    },
    async getUserDevices(): Promise<void> {
      await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
    },
    updateDevicesUpdated(): void {
      const devicesUpdated: UpdatedDevice[] = []
      const setting = addUserSetting({
        devicesUpdated
      })
      this.$api.patchUserSettings(setting)
    },
    updateViewedSubscriptionExpired(value = true): void {
      const setting = addUserSetting({
        viewedSubscriptionExpired: value
      })
      //update store
      this.$store.dispatch(`${NAMESPACES.USER}/${USER_ACTIONS.UPDATE_SETTINGS}`, setting)
      //update api
      this.$api.patchUserSettings(setting)
    },
    updateLVVersion(): void {
      if (this.$store.state.releaseVersion) {
        const versionSetting = addUserSetting({
          lastViewedVersion: this.$store.state.releaseVersion
        })
        this.$api.patchUserSettings(versionSetting)
      }
    },
    getLastReleaseMinorV(): string {
      const rv = this.lastVersion
      const sp = rv.split('.')
      const mv = sp.map(this.removePatch).join('.')
      return mv
    },
    removePatch(x: string, i: number): string {
      if (i == 2) return 'X'
      return x
    },
    startPinActivation(deviceId: string): void {
      const device = {
        deviceId: deviceId,
        coords:
          this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`](
            deviceId
          )
      }
      this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.START_MOVING}`, device)
    },
    async onDeviceCreated(deviceId: string): Promise<void> {
      this.startPinActivation(deviceId)
    },
    async getModelsMeta(): Promise<void> {
      const modelsData = await getModelsMetaData()
      this.$store.dispatch(
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_ACTIONS.SET_MODELS_META_DATA}`,
        modelsData
      )
    }
  }
})
</script>

<style lang="scss" scoped>
.add-device {
  position: absolute;
  bottom: 1.6rem;
  right: 1.6rem;
}

.map-legend {
  position: absolute;
  bottom: 0rem;
  left: 1.6rem;

  @media (max-width: 650px) {
    left: 0;
  }
}
</style>
